// Tags each lead with where the visitor came from, so partner referrals can be
// told apart from our own (organic) traffic in the backend.

// Short landing paths we hand to partners. Each one tags the visitor and then
// redirects to the claim form.
export const PARTNER_PATHS: Record<string, string> = {
    '/forces': 'sentinelforces',
}

// Fallback when a partner links without the tagged path or UTMs. Browsers
// often strip the referrer, so this only catches some of their traffic.
const PARTNER_REFERRER_HOSTS: Record<string, string> = {
    'sentinelforces.co.uk': 'sentinelforces',
}

const STORAGE_KEY = 'lead_attribution'
const ORGANIC = 'organic'

interface Attribution {
    source: string
    referrer: string
    landingUrl: string
    utms: Record<string, string>
}

const readStored = (): Attribution | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        return raw ? JSON.parse(raw) : null
    } catch {
        return null
    }
}

const sourceFromReferrer = (referrer: string) => {
    try {
        const host = new URL(referrer).hostname.replace(/^www\./, '')
        return PARTNER_REFERRER_HOSTS[host] || null
    } catch {
        return null
    }
}

// Where this page load came from: a partner tag (path, utm_source or partner
// referrer) or organic. Sent with the visit ping as-is.
export const detectVisit = (location: { pathname: string; search: string }): Attribution => {
    const params = new URLSearchParams(location.search)
    const utms: Record<string, string> = {}
    params.forEach((value, key) => {
        if (key.startsWith('utm_')) utms[key] = value
    })

    const referrer = document.referrer || ''
    const partnerSource =
        PARTNER_PATHS[location.pathname.replace(/\/+$/, '')] || utms.utm_source || sourceFromReferrer(referrer)

    return {
        source: partnerSource || ORGANIC,
        referrer,
        // From the router location, not window.location: partner paths have
        // already redirected to / by the time this runs
        landingUrl: window.location.origin + location.pathname + location.search,
        utms,
    }
}

// Runs once on first page load and stores the visit for the lead. The first
// partner tag wins; an untagged visit never overwrites a stored partner tag.
export const captureAttribution = (visit: Attribution) => {
    const stored = readStored()
    if (stored && (stored.source !== ORGANIC || visit.source === ORGANIC)) return
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(visit))
    } catch {}
}

// Fields sent with the lead on submit.
export const getAttribution = () => {
    const stored = readStored()
    return {
        leadSource: stored?.source || ORGANIC,
        referrer: stored?.referrer || '',
        landingUrl: stored?.landingUrl || '',
        utms: stored?.utms || {},
    }
}
